import { Button, Eyebrow } from '@tt/design-system';
import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { TextField } from '../../form/TextField';
import {
  CARD_BRAND_LABEL,
  detectBrand,
  last4,
  maskCardNumber,
  maskExpiry,
  minDigitsFor,
} from '../../../utils/payments/mockCard';
import { formatCurrencyBRL } from '../../../utils/payments/mockPix';
import { wait } from '../../../utils/wait';
import type { PaymentMethodProps } from '../types';
import { CardPreview } from './CardPreview';

type CardErrors = {
  number?: string;
  name?: string;
  expiry?: string;
  cvv?: string;
};

export function CardMethod({
  amount,
  onConfirm,
  isConfirming = false,
}: PaymentMethodProps) {
  const [number, setNumber] = useState('');
  const [name, setName] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [authorizing, setAuthorizing] = useState(false);
  const [errors, setErrors] = useState<CardErrors>({});

  const rawDigits = useMemo(() => number.replace(/\D/g, ''), [number]);
  const brand = useMemo(() => detectBrand(rawDigits), [rawDigits]);
  const formattedNumber = useMemo(() => maskCardNumber(rawDigits, brand), [rawDigits, brand]);
  const minDigits = minDigitsFor(brand);
  const cvvMax = brand === 'amex' ? 4 : 3;

  function validate(): boolean {
    const next: CardErrors = {};
    if (rawDigits.length < minDigits) {
      next.number = `Use ao menos ${minDigits} dígitos.`;
    }
    if (name.trim().length < 3) {
      next.name = 'Informe o nome impresso no cartão.';
    }
    if (!/^\d{2}\/\d{2}$/.test(expiry)) {
      next.expiry = 'Use o formato MM/AA.';
    } else {
      const [mm] = expiry.split('/');
      if (Number(mm) < 1 || Number(mm) > 12) next.expiry = 'Mês inválido.';
    }
    if (cvv.replace(/\D/g, '').length < 3) {
      next.cvv = 'CVV incompleto.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (isConfirming || authorizing) return;
    if (!validate()) return;

    setAuthorizing(true);
    await wait(1200); // delay cerimonial "autorizando"
    setAuthorizing(false);

    const details = `${CARD_BRAND_LABEL[brand]} ****${last4(rawDigits)}`;
    onConfirm({ method: 'card', detailsFake: details });
  }

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className="flex flex-col gap-6"
      aria-busy={authorizing}
    >
      <div className="flex items-baseline justify-between gap-4">
        <Eyebrow tone="gold">Cartão de crédito</Eyebrow>
        <span className="font-display text-3xl text-gold-shimmer">
          {formatCurrencyBRL(amount)}
        </span>
      </div>

      <CardPreview number={formattedNumber} name={name} expiry={expiry} brand={brand} />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <TextField
            label="Número do cartão"
            value={formattedNumber}
            inputMode="numeric"
            autoComplete="cc-number"
            placeholder="0000 0000 0000 0000"
            maxLength={brand === 'amex' ? 17 : 23}
            error={errors.number}
            onChange={(e) => {
              const d = e.target.value.replace(/\D/g, '').slice(0, 19);
              setNumber(maskCardNumber(d, detectBrand(d)));
              if (errors.number) setErrors((p) => ({ ...p, number: undefined }));
            }}
          />
        </div>

        <div className="sm:col-span-2">
          <TextField
            label="Nome impresso"
            value={name}
            autoComplete="cc-name"
            placeholder="Como aparece no cartão"
            maxLength={40}
            error={errors.name}
            onChange={(e) => {
              setName(e.target.value);
              if (errors.name) setErrors((p) => ({ ...p, name: undefined }));
            }}
          />
        </div>

        <TextField
          label="Validade"
          value={expiry}
          inputMode="numeric"
          autoComplete="cc-exp"
          placeholder="MM/AA"
          maxLength={5}
          error={errors.expiry}
          onChange={(e) => {
            setExpiry(maskExpiry(e.target.value));
            if (errors.expiry) setErrors((p) => ({ ...p, expiry: undefined }));
          }}
        />

        <TextField
          label="CVV"
          value={cvv}
          type="password"
          inputMode="numeric"
          autoComplete="cc-csc"
          placeholder={brand === 'amex' ? '0000' : '000'}
          maxLength={cvvMax}
          error={errors.cvv}
          onChange={(e) => {
            setCvv(e.target.value.replace(/\D/g, ''));
            if (errors.cvv) setErrors((p) => ({ ...p, cvv: undefined }));
          }}
        />
      </div>

      <div className="border-t border-gold/15 pt-6">
        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          disabled={isConfirming || authorizing}
        >
          {isConfirming
            ? 'Eternizando seu registro…'
            : authorizing
              ? 'Autorizando transação…'
              : `Pagar ${formatCurrencyBRL(amount)}`}
        </Button>
        <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-stamp text-champagne/60">
          Modo demonstração · qualquer número de cartão será aceito
        </p>
      </div>
    </form>
  );
}
